import { logger } from '@/utils/logger';

/**
 * Auth-path observability. The mobile social-auth flow was failing SILENTLY on
 * real devices (Rollbar empty, only dev-console logs), so we were debugging
 * blind. These helpers leave a visible trail for BOTH the dev console AND
 * Rollbar (in prod), at every key step of the redirect/popup + trial-provision
 * flow.
 *
 * PII rule: never pass email/name/tokens — `uid`, provider, error.code, and
 * environment flags only.
 *
 * Rollbar is loaded lazily (matching `prodLogger`) so these breadcrumbs don't
 * pull the Rollbar SDK into the initial bundle.
 */
type Meta = Record<string, unknown>;

/** Info-level breadcrumb: reaches the dev console always, and Rollbar in prod. */
export function authBreadcrumb(step: string, meta?: Meta): void {
  logger.info('Auth', step, meta);
  void import('@/services/errorMonitoring')
    .then(({ errorMonitoring }) => {
      errorMonitoring.logInfo(`Auth: ${step}`, {
        component: 'Auth',
        action: step,
        metadata: meta,
      });
    })
    .catch(() => {
      /* monitoring is best-effort — never let a breadcrumb throw */
    });
}

/** Failure on the auth path → a visible Rollbar ERROR item (never swallowed). */
export function authError(step: string, error: unknown, meta?: Meta): void {
  const err = error instanceof Error ? error : new Error(String(error));
  const code = (error as { code?: string })?.code;
  logger.error('Auth', step, { error, ...meta });
  void import('@/services/errorMonitoring')
    .then(({ errorMonitoring }) => {
      errorMonitoring.logError(err, {
        component: 'Auth',
        action: step,
        level: 'error',
        metadata: { ...(code ? { code } : {}), ...meta },
      });
    })
    .catch(() => {
      /* monitoring is best-effort */
    });
}
